import classNames from 'classnames'
import dynamic from 'next/dynamic'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import BurgerIcon from '../../assets/icons/burger.svg'
import ArrowIcon from '../../assets/icons/chord-editor/arrow.svg'
import CloseIcon from '../../assets/icons/chord-editor/close.svg'
import ProfileIcon from '../../assets/icons/sidebar-layout/user.svg'
import { useCurrentUser } from '../../context/CurrentUserContext'
import { useGlobalToolConfig } from '../../context/GlobalToolConfigContext'
import { useInternationalization } from '../../context/InternationalizationContext'
import { usePaddleBilling } from '../../context/PaddleBillingContext'
import { clickOutsideHookIds } from '../../hooks/clickOutsideAlerterHook'
import useSizes from '../../hooks/useSizes'
import { getDiskForUserSubscription } from '../../utils/pricing'
import PageLoader from '../common/PageLoader/PageLoader'
import Tooltip from '../common/Tooltip/Tooltip'
import styles from './SidebarLayout.module.scss'
import NotifyAboutSoonToolModal from './components/NotifyAboutSoonToolModal'
import TryProButton from './components/TryProButton'

const SidebarMobileDropdowns = dynamic(() => import('./components/SidebarMobileDropdowns'))
const ProfileMenu = dynamic(() => import('./components/ProfileMenu'))

export enum SIDEBAR_TAB_KEYS {
  HOME = 'HOME',
  STUDIO = 'STUDIO',
  TEMPLATES = 'TEMPLATES',
  TRACK_SEPARATION = 'TRACK_SEPARATION',
  MASTERING = 'MASTERING',
  LEARN_CHORDS = 'LEARN_CHORDS',
  MINIMIZE_SIDEBAR = 'MINIMIZE_SIDEBAR',
}
export type TSidebarTabKeys = keyof typeof SIDEBAR_TAB_KEYS
export type TSidebarTab = {
  key: TSidebarTabKeys
  icon: ReactNode
  name: string
  isSoon?: boolean
  component?: any
  onClick?: any
  href?: string
}

const SidebarLayout = () => {
  const { isMobile } = useSizes()
  const { currentUser } = useCurrentUser()
  const { getText } = useInternationalization()
  const { getPlanName } = usePaddleBilling()
  const {
    pageLoading,
    setPageLoading,
    activeSidebarTabKey,
    sidebarLocked,
    sidebarOpen,
    setSidebarOpen,
    homeTab,
    toolTabs,
    minimizeTab,
    handleOpenProject,
    studioProjectId,
    trackSeparationProjectId,
    masteringProjectId,
  } = useGlobalToolConfig()

  const sidebarOpenTimeout = useRef<any>(null)

  const [profileMenuOpen, setProfileMenuOpen] = useState(false)

  const STRINGS = getText('SidebarLayout')
  const tabGroups = [[homeTab], ...toolTabs]

  const ActiveTabComponent = useMemo(() => {
    const tabs = [homeTab, ...toolTabs.flat()]
    const { component } = tabs.find((tab) => tab.key === activeSidebarTabKey) || tabs[0]
    if (!component) return null

    return dynamic(component) as any
  }, [activeSidebarTabKey])
  const isSidebarAllwaysOpen = useMemo(() => {
    switch (activeSidebarTabKey) {
      case SIDEBAR_TAB_KEYS.HOME:
      case SIDEBAR_TAB_KEYS.LEARN_CHORDS:
      case SIDEBAR_TAB_KEYS.TEMPLATES:
        return true
      case SIDEBAR_TAB_KEYS.STUDIO:
        return !studioProjectId
      case SIDEBAR_TAB_KEYS.TRACK_SEPARATION:
        return true
        return !trackSeparationProjectId
      case SIDEBAR_TAB_KEYS.MASTERING:
        return true
        return !masteringProjectId
      default:
        return false
    }
  }, [activeSidebarTabKey, studioProjectId, trackSeparationProjectId, masteringProjectId])

  useEffect(() => {
    import('./components/ProfileMenu')

    if (isMobile) import('./components/SidebarMobileDropdowns')
  }, [currentUser, isMobile])

  const isSidebarLocked = !isSidebarAllwaysOpen && sidebarLocked && !isMobile
  const isSidebarFullWidth = profileMenuOpen || sidebarOpen || isSidebarAllwaysOpen || isMobile

  const handleChangeSidebarTab = (tab: TSidebarTab) => {
    setTimeout(() => {
      handleOpenProject(undefined, tab.key)
    }, 0)
  }
  const handleAccountBtnClick = () => {
    setProfileMenuOpen((v) => !v)
  }

  const renderHeaderLogo = () => {
    return (
      <div className={styles.sidebarHeaderLogo} onClick={() => handleOpenProject(undefined, SIDEBAR_TAB_KEYS.HOME)}>
        {!isSidebarFullWidth || isSidebarLocked ? 'CC' : 'ChordChord'}
      </div>
    )
  }
  const renderContentTab = (tab: TSidebarTab) => {
    const isActive = tab.key === activeSidebarTabKey
    const tooltipId = `sidebar-tab-${tab.key}`

    return (
      <>
        <div
          key={tab.key}
          className={classNames(styles.sidebarContentTab, { [styles.sidebarContentTabActive]: isActive })}
          onClick={() => {
            if (tab.onClick) {
              tab.onClick()
              return
            }
            if (isActive) return

            handleChangeSidebarTab(tab)
          }}
          data-tooltip-id={tooltipId}
        >
          <div className={styles.tabIcon}>{tab.icon}</div>

          <div className={styles.tabContent}>
            <div className={styles.tabName}>{tab.name}</div>
            {tab.isSoon && <div className={styles.tabSoon}>{STRINGS.soon}</div>}
          </div>
        </div>

        <Tooltip
          className={classNames(styles.sidebarContentTabTooltip, {
            [styles.sidebarContentTabTooltipHidden]: !sidebarLocked || isSidebarAllwaysOpen,
          })}
          delayShow={500}
          id={tooltipId}
          text={tab.name}
          place='right'
        />
      </>
    )
  }
  const renderFooterProfile = () => {
    const isLoggedIn = !!currentUser
    const isFree = isLoggedIn && currentUser.subscriptionPlan === 'free'
    const isPremium = isLoggedIn && !isFree

    const isCreator = isPremium && currentUser.subscriptionPlan.includes('creator_')
    const isPro = isPremium && currentUser.subscriptionPlan.includes('pro_')

    return (
      <div
        id={clickOutsideHookIds.sidebarProfile}
        className={classNames(
          styles.sidebarFooterProfile,
          { [styles.sidebarFooterProfileCreator]: isCreator },
          { [styles.sidebarFooterProfilePro]: isPro },
          { [styles.sidebarFooterProfileOpen]: profileMenuOpen },
        )}
        onClick={handleAccountBtnClick}
      >
        <div className={styles.profileIcon}>
          <div className={styles.profileIconInner}>
            {isLoggedIn ? (
              <span>{currentUser?.name.slice(0, 2)}</span>
            ) : (
              <ProfileIcon className={styles.profileIconUser} />
            )}

            {currentUser && getDiskForUserSubscription(currentUser.subscriptionPlan, styles.profileIconDisk)}
          </div>
        </div>

        <div className={styles.profileContent}>
          <div className={styles.profileContentInfo}>
            <div className={styles.profileContentInfoName}>{currentUser?.name || STRINGS.account}</div>

            {isLoggedIn && (
              <div className={styles.profileContentInfoPlan}>{getPlanName(currentUser.subscriptionPlan)}</div>
            )}
          </div>

          <ArrowIcon className={styles.profileContentArrow} />
        </div>
      </div>
    )
  }
  const renderFooterProfileMenu = () => {
    if (!profileMenuOpen) return null
    return <ProfileMenu activeTabKey={activeSidebarTabKey} setProfileMenuOpen={setProfileMenuOpen} />
  }

  const renderMobileSidebarHeader = () => {
    if (activeSidebarTabKey === SIDEBAR_TAB_KEYS.STUDIO && studioProjectId) return null
    return (
      <div className={styles.mobileSidebarHeader}>
        <div className={styles.mobileSidebarHeaderBurger} onClick={() => setSidebarOpen(true)}>
          <BurgerIcon />
        </div>

        {renderHeaderLogo()}
      </div>
    )
  }
  const renderMobileCloseBtn = () => {
    if (!isMobile) return null
    return (
      <div className={styles.mobileCloseBtn} onClick={() => setSidebarOpen(false)}>
        <CloseIcon />
      </div>
    )
  }
  const renderMobileTryProBtn = () => {
    if (!isMobile) return null
    return <TryProButton showAlways />
  }
  const renderMobileDropdowns = () => {
    if (!isMobile) return null
    return <SidebarMobileDropdowns />
  }

  const renderDektopLockBtn = () => {
    if (isMobile) return null
    if (isSidebarAllwaysOpen) return null
    return (
      <div
        className={classNames(styles.sidebarLockerContainer, { [styles.sidebarLockerContainerActive]: sidebarLocked })}
      >
        {renderContentTab(minimizeTab)}
      </div>
    )
  }

  const renderSidebar = () => {
    return (
      <div
        className={classNames(
          styles.sidebar,
          { [styles.sidebarLocked]: isSidebarLocked },
          { [styles.sidebarOpen]: isSidebarFullWidth },
        )}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => {
          if (isMobile) return

          clearTimeout(sidebarOpenTimeout.current)

          sidebarOpenTimeout.current = setTimeout(() => {
            if (!sidebarOpen) setSidebarOpen(true)
          }, 300)
        }}
        onMouseLeave={() => {
          if (isMobile) return

          clearTimeout(sidebarOpenTimeout.current)

          sidebarOpenTimeout.current = setTimeout(() => {
            if (sidebarOpen) setSidebarOpen(false)
          }, 0)
        }}
      >
        <div className={styles.sidebarHeader}>
          {renderHeaderLogo()}

          {renderMobileCloseBtn()}
        </div>

        <div className={styles.sidebarContent}>
          {renderMobileTryProBtn()}

          <div className={styles.sidebarContentTabs}>
            {tabGroups.map((tabs, index) => (
              <>
                {!!index && <div className={styles.sidebarContentDivider} />}

                {tabs.map((tab) => renderContentTab(tab))}
              </>
            ))}
          </div>

          {renderMobileDropdowns()}
          {renderDektopLockBtn()}
        </div>

        <div className={styles.sidebarFooter}>
          {renderFooterProfile()}
          {renderFooterProfileMenu()}
        </div>
      </div>
    )
  }
  const renderSidebarContainer = () => {
    if (isMobile) {
      return (
        <>
          {renderMobileSidebarHeader()}

          <div
            className={classNames(styles.mobileSidebarContainerBg, {
              [styles.mobileSidebarContainerBgOpen]: sidebarOpen,
            })}
            onClick={() => setSidebarOpen(false)}
          />
          <div
            className={classNames(styles.mobileSidebarContainer, { [styles.mobileSidebarContainerOpen]: sidebarOpen })}
          >
            {renderSidebar()}
          </div>
        </>
      )
    }
    return (
      <div
        className={classNames(styles.sidebarContainer, { [styles.sidebarContainerAllwaysOpen]: isSidebarAllwaysOpen })}
      >
        {renderSidebar()}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {renderSidebarContainer()}

      <div className={styles.tabContainer}>
        {ActiveTabComponent && <ActiveTabComponent pageLoading={pageLoading} setPageLoading={setPageLoading} />}
        <PageLoader loading={pageLoading} className={styles.pageLoading} />

        <NotifyAboutSoonToolModal />
      </div>
    </div>
  )
}

export default SidebarLayout
